import { router, usePage } from "@inertiajs/react"
import * as Dialog from "@radix-ui/react-dialog"
import { Command } from "cmdk"
import { ArrowUpRight, Search, ShieldCheck, Star, X } from "lucide-react"
import { useState, type CSSProperties } from "react"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { useShellNavUrl } from "@/hooks/use-shell-nav-url"
import { useStoredIds } from "@/hooks/use-stored-ids"
import { useTranslation } from "@/hooks/use-translation"
import {
  ADMIN_MENU_COLUMNS,
  ADMIN_MENU_ITEMS,
  adminMenuItemActive,
  findAdminMenuItem,
  type AdminMenuCounters,
  type AdminMenuItem,
} from "@/lib/admin-menu"
import { beginShellNav } from "@/lib/shell-nav-pending"
import { filterBySubstring } from "@/lib/substring-filter"
import { cn } from "@/lib/utils"

const FAVORITES_KEY = "it2tv:adminMenu:favorites"
const RECENT_KEY = "it2tv:adminMenu:recent"
const RECENT_LIMIT = 6

type BadgeTone = "amber" | "danger"

const ROW_CLASS =
  "flex h-9 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg pr-1.5 pl-2.5 text-left text-sm text-ui-text-secondary"
const ROW_ACTIVE_CLASS = "bg-[var(--ui-nav-admin-active-bg)] text-[var(--ui-nav-admin-fg)]"
const SECTION_LABEL_CLASS = "px-1 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase"

/**
 * Счётчик: янтарный — «требует внимания» (платёжки), danger — без решения что-то
 * в системе не обновится (конфликты системных промптов).
 */
function CountBadge({ count, tone, compact }: { count: number; tone: BadgeTone; compact?: boolean }) {
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-full text-[10px] font-semibold tabular-nums",
        tone === "danger"
          ? "bg-ui-danger text-ui-on-accent"
          : "bg-[var(--ui-badge-amber-bg)] text-[var(--ui-badge-amber-fg)] ring-1 ring-[var(--ui-badge-amber-border)]",
        // На кнопке бейдж висит в углу. Только вверх, не вправо:
        // узкая колонка свёрнутого сайдбара срезала бы выступающий край.
        compact ? "absolute -top-1 right-0 min-w-4 px-1 py-px" : "ml-auto min-w-5 shrink-0 px-1.5 py-0.5",
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  )
}

/** Кнопка «Администрирование» внизу сайдбара и панель со всеми разделами платформы. */
export function AdminMenu({
  url: serverUrl,
  collapsed,
  onAction,
}: {
  url: string
  collapsed: boolean
  onAction: () => void
}) {
  const url = useShellNavUrl(serverUrl)
  const { t } = useTranslation(["layout"])
  // Ключи подписей приходят из конфига строками.
  const label = (key: string) => t(key, { defaultValue: key })
  const counters = usePage<AdminMenuCounters>().props
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [tab, setTab] = useState("favorites")
  const [favorites, setFavorites] = useStoredIds(FAVORITES_KEY)
  const [recent, setRecent] = useStoredIds(RECENT_KEY)

  const current = findAdminMenuItem(url)
  const countOf = (item: AdminMenuItem) => (item.badge ? (counters[item.badge.prop] ?? 0) : 0)
  const attention = ADMIN_MENU_ITEMS.filter((item) => countOf(item) > 0)
  const attentionTotal = attention.reduce((sum, item) => sum + countOf(item), 0)
  const attentionTone: BadgeTone = attention.some((item) => item.badge?.tone === "danger") ? "danger" : "amber"
  const quickHrefs = tab === "favorites" ? favorites : recent
  const quickItems = quickHrefs.flatMap((href) => ADMIN_MENU_ITEMS.find((item) => item.href === href) ?? [])
  // Панель прибита к правому краю сайдбара; ниже md — на весь экран.
  const anchorStyle = {
    "--admin-menu-left": collapsed ? "3.5rem" : "var(--shell-sidebar-width)",
  } as CSSProperties

  const openChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSearch("")
  }

  const go = (item: AdminMenuItem) => {
    openChange(false)
    onAction()
    setRecent([item.href, ...recent.filter((href) => href !== item.href)].slice(0, RECENT_LIMIT))
    if (item.fullPage) {
      window.location.assign(item.href)
      return
    }
    beginShellNav(item.href)
    router.visit(item.href)
  }

  const toggleFavorite = (href: string) =>
    setFavorites(favorites.includes(href) ? favorites.filter((id) => id !== href) : [...favorites, href])

  const itemBody = (item: AdminMenuItem) => {
    const Icon = item.icon
    const count = countOf(item)
    return (
      <>
        <Icon className="h-4 w-4 shrink-0 opacity-70" />
        <span className="min-w-0 flex-1 truncate">{label(item.labelKey)}</span>
        {item.fullPage ? <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ui-text-faint" /> : null}
        {item.badge && count > 0 ? <CountBadge count={count} tone={item.badge.tone} /> : null}
      </>
    )
  }

  // Строка боковой колонки: избранное, недавнее, «требует внимания».
  const sideRow = (item: AdminMenuItem) => (
    <button
      key={item.href}
      type="button"
      onClick={() => go(item)}
      className={cn(
        ROW_CLASS,
        adminMenuItemActive(item, url) ? ROW_ACTIVE_CLASS : "hover:bg-ui-surface-raised hover:text-ui-text-primary",
      )}
    >
      {itemBody(item)}
    </button>
  )

  const trigger = (
    <Dialog.Trigger
      type="button"
      aria-label={collapsed ? t("shell.admin.title") : undefined}
      // Цвета обычного пункта сайдбара; активен, пока открыта панель или открыт админский раздел.
      className={cn(
        "relative flex items-center rounded-lg transition-colors",
        open || current
          ? "bg-[var(--ui-nav-active-bg)] text-[var(--ui-nav-active-fg)] light:shadow-[var(--shadow-clay-inset)]"
          : "text-[var(--ui-nav-idle-fg)] hover:bg-[var(--ui-nav-idle-hover-bg)] hover:text-[var(--ui-nav-idle-hover-fg)]",
        collapsed ? "h-9 w-9 justify-center" : "w-full gap-2 px-3 py-1.5 text-left",
      )}
    >
      <ShieldCheck className="h-4 w-4 shrink-0" />
      {!collapsed ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm leading-5">{t("shell.admin.title")}</span>
          {current ? (
            <span className="truncate text-[11px] leading-4 opacity-70">
              {label(current.group.titleKey)} · {label(current.item.labelKey)}
            </span>
          ) : null}
        </span>
      ) : null}
      {/* Бейдж в углу кнопки: в развёрнутом сайдбаре иначе не помещается заголовок. */}
      {attentionTotal > 0 ? <CountBadge count={attentionTotal} tone={attentionTone} compact /> : null}
    </Dialog.Trigger>
  )

  return (
    <Dialog.Root open={open} onOpenChange={openChange}>
      {collapsed ? <SidebarTooltip label={t("shell.admin.title")}>{trigger}</SidebarTooltip> : trigger}
      <Dialog.Portal>
        {/* Ниже хедера и справа от сайдбара: только основной блок. */}
        <Dialog.Overlay
          style={anchorStyle}
          className="ui-dialog-overlay fixed inset-0 z-[150] bg-[var(--ui-overlay)] md:top-[var(--shell-window-bar-height)] md:left-[var(--admin-menu-left)]"
        />
        <Dialog.Content
          style={anchorStyle}
          aria-describedby={undefined}
          className={cn(
            "ui-dialog-fade fixed inset-0 z-[150] flex flex-col overflow-hidden bg-ui-bg-elevated text-ui-text-primary focus:outline-none",
            "md:top-[calc(var(--shell-window-bar-height)+0.5rem)] md:right-auto md:bottom-2 md:left-[calc(var(--admin-menu-left)+0.5rem)]",
            "md:w-[min(65rem,calc(100vw-var(--admin-menu-left)-1rem))] md:rounded-lg md:border md:border-ui-border md:shadow-2xl md:shadow-black/40",
          )}
        >
          <Dialog.Title className="sr-only">{t("shell.admin.title")}</Dialog.Title>
          <Command loop filter={filterBySubstring} className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-ui-border pr-2 pl-4">
              <Search className="h-4 w-4 shrink-0 text-ui-text-muted" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={t("shell.admin.search")}
                className="h-10 min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ui-text-primary outline-none placeholder:text-ui-text-faint focus:ring-0"
              />
              <Dialog.Close
                type="button"
                className="rounded-lg p-2 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("shell.admin.close")}
              >
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>

            <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[14.5rem_minmax(0,1fr)] md:grid-rows-1">
              <aside
                className={cn(
                  "flex flex-col gap-5 border-b border-ui-border p-3 md:overflow-y-auto md:border-r md:border-b-0",
                  search && "max-md:hidden",
                )}
              >
                <div className="flex flex-col gap-2">
                  <SegmentedControl
                    value={tab}
                    onChange={setTab}
                    className="self-start"
                    items={[
                      { id: "favorites", label: t("shell.admin.favorites") },
                      { id: "recent", label: t("shell.admin.recent") },
                    ]}
                  />
                  {quickItems.length > 0 ? (
                    <div className="flex flex-col gap-0.5">{quickItems.map(sideRow)}</div>
                  ) : (
                    <p className="px-1 text-xs leading-relaxed text-ui-text-muted">
                      {tab === "favorites" ? t("shell.admin.favoritesEmpty") : t("shell.admin.recentEmpty")}
                    </p>
                  )}
                </div>

                {attention.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    <div className={SECTION_LABEL_CLASS}>{t("shell.admin.attention")}</div>
                    {attention.map(sideRow)}
                  </div>
                ) : null}
              </aside>

              <Command.List className="@container min-h-0 overflow-y-auto px-3 py-4 md:px-5 md:py-5">
                <Command.Empty className="px-2.5 py-10 text-sm text-ui-text-muted">
                  {t("shell.admin.noResults")}
                </Command.Empty>
                {/* Колонки по ширине панели, а не окна: панель уже окна на ширину сайдбара. */}
                <div className="grid items-start gap-x-6 gap-y-7 @md:grid-cols-2 @2xl:grid-cols-3">
                  {ADMIN_MENU_COLUMNS.map((groups) => (
                    <div key={groups[0].id} className="contents @2xl:flex @2xl:min-w-0 @2xl:flex-col @2xl:gap-7">
                      {groups.map((group) => (
                        <Command.Group
                          key={group.id}
                          heading={label(group.titleKey)}
                          className="min-w-0 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-[13px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-ui-text-primary [&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-col [&_[cmdk-group-items]]:gap-0.5"
                        >
                          {group.items.map((item) => {
                            const favorite = favorites.includes(item.href)
                            return (
                              <Command.Item
                                key={item.href}
                                value={item.href}
                                keywords={[label(item.labelKey), label(group.titleKey), item.keywords]}
                                onSelect={() => go(item)}
                                className={cn(
                                  ROW_CLASS,
                                  "group",
                                  adminMenuItemActive(item, url)
                                    ? ROW_ACTIVE_CLASS
                                    : "data-[selected=true]:bg-ui-surface-raised data-[selected=true]:text-ui-text-primary",
                                )}
                              >
                                {itemBody(item)}
                                <button
                                  type="button"
                                  // Звезда — для мыши: Enter в cmdk открывает раздел, а не переключает избранное.
                                  tabIndex={-1}
                                  aria-pressed={favorite}
                                  aria-label={favorite ? t("shell.admin.removeFavorite") : t("shell.admin.addFavorite")}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    toggleFavorite(item.href)
                                  }}
                                  className={cn(
                                    "flex size-6 shrink-0 items-center justify-center rounded-md text-ui-text-faint transition-opacity",
                                    "hover:bg-[var(--ui-nav-admin-bg)] hover:text-[var(--ui-nav-admin-fg)]",
                                    favorite
                                      ? "text-[var(--ui-nav-admin-fg)]"
                                      : "opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100",
                                  )}
                                >
                                  <Star className={cn("h-3.5 w-3.5", favorite && "fill-current")} />
                                </button>
                              </Command.Item>
                            )
                          })}
                        </Command.Group>
                      ))}
                    </div>
                  ))}
                </div>
              </Command.List>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
